import invariant from 'tiny-invariant';
import { CouponItem } from '../../generated/api';
import { Coupon, CouponType } from '../../models/ClientModels';
import { apiServerTimeToDateObject } from '../api-model-utils/apiServerTimeToDateObject';
import { UniversalCoupon } from '../models/coupon';

export function couponItemToUniversalCoupon(
  coupon: CouponItem | Coupon,
): null | UniversalCoupon {
  const type = coupon.type as unknown as CouponType;
  switch (type) {
    case CouponType.discountAmount:
      return couponItemToUniversalAmountCoupon(coupon);
    case CouponType.freeShipping:
      return couponItemToUniversalFreeShippingCoupon(coupon);
    case CouponType.discountPercent:
      return couponItemToUniversalPercentageCoupon(coupon);
    default:
      return null;
  }
}

function couponItemToUniversalAmountCoupon(
  coupon: CouponItem | Coupon,
): UniversalCoupon.AmountCoupon {
  invariant(
    coupon.type === CouponType.discountAmount,
    'Expected discountAmount coupon type',
  );
  return {
    ...couponItemToUniversalCouponCommon(coupon),
    type: CouponType.discountAmount,
    discountAmount: coupon.discountAmount!,
    minimalPurchase: coupon.minimalPurchase,
  };
}

function couponItemToUniversalPercentageCoupon(
  coupon: CouponItem | Coupon,
): UniversalCoupon.PercentCoupon {
  invariant(
    coupon.type === CouponType.discountPercent,
    'Expected discountPercent coupon type',
  );
  return {
    ...couponItemToUniversalCouponCommon(coupon),
    type: CouponType.discountPercent,
    discountPercent: coupon.discountPercent!,
    maximumDiscount: coupon.maximumDiscount,
    excludedDropshipPartnerIds: coupon.excludedDropshipPartnerIds,
    applicableOrderType: coupon.applicableOrderType! as any,
  };
}

function couponItemToUniversalFreeShippingCoupon(
  coupon: CouponItem | Coupon,
): UniversalCoupon.FreeShippingCoupon {
  invariant(
    coupon.type === CouponType.freeShipping,
    'Expected freeShipping coupon type',
  );
  return {
    ...couponItemToUniversalCouponCommon(coupon),
    type: CouponType.freeShipping,
    minimalPurchase: coupon.minimalPurchase,
  };
}

function couponItemToUniversalCouponCommon(
  coupon: CouponItem | Coupon,
): UniversalCoupon.CouponCommon {
  return {
    code: coupon.code,
    enabled: coupon.enabled,
    quantity: coupon.quantityLimit ? coupon.quantity : null,
    redeemedQuantity: coupon.redeemedQuantity,
    startedAt: apiServerTimeToDateObject(coupon.startedAt),
    expiredAt: apiServerTimeToDateObject(coupon.expiredAt),
    createdAt: apiServerTimeToDateObject(coupon.createdAt),
    storeIds: coupon.storeIds,
    couponCreateMethod: coupon.couponCreateMethod,
    onlyNewUser: coupon.onlyNewUser,
    onlyPurchasedUser: coupon.onlyPurchasedUser,
    onlyFollower: coupon.onlyFollower,
    userGroupIds: coupon.userGroupIds,
    oncePerStore: coupon.oncePerStore,
    onlyNewUserPerStore: coupon.onlyNewUserPerStore,
    onlyReactivatedUser: coupon.onlyReactivatedUser,
    sellerGroupIds: coupon.sellerGroupIds,
    liveStreamIds: coupon.liveStreamIds,
    name: coupon.name,
    description: coupon.description,
    imageUrl: coupon.imageUrl,
    pagUrl: coupon.pagUrl,
  };
}
